import { useEffect, useState } from 'react';
import Button from '@/components/Button';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import NumberInput from '@/components/NumberInput';
import Pagination from '@/components/Pagination';
import DiscountStar from '@/modules/store/components/DiscountStar';
import ImageThumbnail from '@/modules/store/components/ImageThumbnail';
import Price from '@/modules/store/components/Price';
import { formatNetContent } from '@/modules/store/models/NetContent';
import { EmptyResult } from '@/modules/store/models/Pagination';
import { Sku, Skus } from '@/modules/store/models/Sku';
import { ProductDetailsPath } from '@/modules/store/paths';
import ProductPriceService from '@/modules/store/services/ProductPriceService';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';

interface ProductCatalogResultsProps {
  skus?: Skus;
  isLoading: boolean;
  isFetching?: boolean;
  page: number;
  itemsPerPage: number;
  onChangePage: (page: number) => void;
  onAddToCart: (sku: Sku, quantity: number) => void;
}

interface SkuQuantities {
  [skuId: string]: number;
}

type State = Skus & {
  quantities: SkuQuantities;
};

const initialState = {
  ...EmptyResult<Sku>(),
  quantities: {},
};

const productPriceService = new ProductPriceService();

const ProductCatalogResults = ({
  skus,
  isLoading,
  isFetching,
  page,
  itemsPerPage,
  onChangePage,
  onAddToCart,
}: ProductCatalogResultsProps) => {
  const navigate = useNavigate();

  const [state, setState] = useState<State>(initialState);

  useEffect(() => {
    setState((prevState): State => {
      if (isLoading || (isLoading && isFetching)) {
        return prevState;
      }

      return {
        items: skus?.items || [],
        totalCount: skus?.totalCount || 0,
        estimatedTotalCount: skus?.estimatedTotalCount || 0,
        quantities:
          skus?.items.reduce((acc: SkuQuantities, sku: Sku) => {
            acc[sku.id] = 1;
            return acc;
          }, {}) || {},
      };
    });
  }, [skus, isLoading, isFetching]);

  const handleQuantityChange = (skuId: string, newQuantity: number) => {
    setState((prevState) => ({
      ...prevState,
      quantities: {
        ...prevState.quantities,
        [skuId]: newQuantity,
      },
    }));
  };

  const handleAddToCartClick = (sku: Sku) => {
    const quantity = state.quantities[sku.id] || sku.moq || 1;
    onAddToCart(sku, quantity);
    toast.custom((t) => (
      <CustomToast
        id={t.id}
        message={`Successfully added ${sku.name} to the cart!`}
        visible={t.visible}
      />
    ));
  };

  const { items, quantities, totalCount, estimatedTotalCount } = state;

  return (
    <div id="product-catalog-table" data-testid="product-catalog" className="w-full">
      <div className="hidden md:block table-container table-custom">
        <div className="table-wrapper">
          <table className={`table ${isLoading ? 'table-loading' : ''}`}>
            <thead className="table-header">
              <tr className="table-header-row">
                <th className="px-3 py-4 pl-6 text-left text-gray-600 font-semibold text-sm w-[40%]">
                  Product Name
                </th>
                <th className="table-cell">SKU</th>
                <th className="table-cell">Price</th>
                <th className="px-3 py-4 pl-12 text-left text-gray-600 font-semibold text-sm w-48">
                  Order Quantity
                </th>
              </tr>
            </thead>
            <tbody className="table-body">
              {items.map((sku, index) => {
                const unitPrice = productPriceService.calculateUnitPrice(sku);
                return (
                  <tr
                    key={sku.id + index}
                    className="table-row"
                    data-testid="product-catalog-item"
                    data-sku-id={sku.id}
                    data-sku-name={sku.name}
                    data-supplier-id={sku.supplierId}
                    data-category-name={sku.product?.productCategory?.name || ''}
                    data-promotion-count={sku.promotions?.length || 0}
                  >
                    <td className="px-3 py-1">
                      <div className="flex gap-4 items-center">
                        <div
                          className="relative pr-4 cursor-pointer hover:opacity-80 transition"
                          onClick={() => navigate(ProductDetailsPath.withId(sku.id))}
                        >
                          {unitPrice && <DiscountStar price={unitPrice} />}
                          <ImageThumbnail image={sku.images} />
                        </div>
                        <div className="gap-y-1">
                          <div className="font-medium">
                            <Link to={ProductDetailsPath.withId(sku.id)}>{sku.name}</Link>
                          </div>
                          <div className="text-sm text-gray-500 flex flex-wrap gap-x-4">
                            {(sku?.moq || 0) > 1 && <span>Min. Order Qty: {sku.moq}</span>}
                            {(sku.quantity || 0) > 0 && <span>Unit count: {sku.quantity}</span>}
                            {sku.netContent && (
                              <span>Net content: {formatNetContent(sku.netContent)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <Link to={ProductDetailsPath.withId(sku.id)}>{sku.externalCode}</Link>
                    </td>
                    <td className="table-cell">
                      <Price price={unitPrice} />
                    </td>
                    <td className="pr-6 px-3 py-3">
                      <div className="flex gap-4 lg:gap-6 flex-col lg:flex-row">
                        <NumberInput
                          data-testid="product-catalog-quantity-input"
                          className="w-full lg:w-16"
                          value={quantities[sku.id]}
                          onChange={(newQuantity) => handleQuantityChange(sku.id, newQuantity)}
                        />
                        <Button
                          data-testid="product-catalog-add-to-cart"
                          onClick={() => handleAddToCartClick(sku)}
                          icon={<ShoppingCartIcon className="w-5 h-5 mr-2" />}
                          variant="primary"
                          size="sm"
                          nowrap
                        >
                          Add to Cart
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isLoading && (
            <div role="status" className="table-loader">
              <SpinnerIcon />
              <span className="sr-only">Loading...</span>
            </div>
          )}
        </div>
      </div>

      <div className="md:hidden space-y-4">
        {items.map((sku, index) => {
          const unitPrice = productPriceService.calculateUnitPrice(sku);
          return (
            <div
              key={sku.id + index}
              className="border rounded-xl p-4 shadow-sm bg-white"
              data-testid="product-catalog-card"
              data-sku-id={sku.id}
              data-sku-name={sku.name}
              data-supplier-id={sku.supplierId}
              data-category-name={sku.product?.productCategory?.name || ''}
              data-promotion-count={sku.promotions?.length || 0}
            >
              <div className="flex gap-4">
                <div
                  className="relative pr-4 cursor-pointer hover:opacity-80 transition"
                  onClick={() => navigate(ProductDetailsPath.withId(sku.id))}
                >
                  {unitPrice && <DiscountStar price={unitPrice} />}
                  <ImageThumbnail image={sku.images} />
                </div>
                <div className="flex-1">
                  <div className="font-semibold">
                    <Link to={ProductDetailsPath.withId(sku.id)}>{sku.name}</Link>
                  </div>
                  <div className="text-sm text-gray-600">
                    <Link to={ProductDetailsPath.withId(sku.id)}>SKU: {sku.externalCode}</Link>
                  </div>
                  {(sku.moq || 0) > 1 && (
                    <div className="text-sm text-gray-600">MOQ: {sku.moq}</div>
                  )}
                  {(sku.quantity || 0) > 0 && (
                    <div className="text-sm text-gray-600">Unit Count: {sku.quantity}</div>
                  )}
                  {sku.netContent && (
                    <div className="text-sm text-gray-600">
                      Net content: <span>{formatNetContent(sku.netContent)}</span>
                    </div>
                  )}
                  <div className="mt-2 text-primary font-semibold">
                    <Price price={unitPrice} />
                  </div>
                </div>
              </div>
              <div className="mt-4 flex gap-4">
                <NumberInput
                  data-testid="product-catalog-quantity-input"
                  className="w-full"
                  value={quantities[sku.id]}
                  onChange={(newQuantity) => handleQuantityChange(sku.id, newQuantity)}
                />
                <Button
                  data-testid="product-catalog-add-to-cart"
                  onClick={() => handleAddToCartClick(sku)}
                  icon={<ShoppingCartIcon className="w-5 h-5 mr-2" />}
                  variant="primary"
                  size="sm"
                  nowrap
                  className="flex-1"
                >
                  Add to Cart
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={totalCount || 0}
          estimatedTotalCount={estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default ProductCatalogResults;
